import {
  Badge,
  Button,
  Icon,
  ReactionBar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  UserAvatar,
} from "@nexus/ui";
import {
  REACTION_TYPES,
  type ReactionActor,
  type ReactionType,
} from "@nexus/contracts";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  formatFullLocalDateTime,
  formatRelativeOrDate,
} from "@/features/opportunities/lib/format-relative-time";

import type {
  ActivityFilter,
  ActivitySegment,
  ActivitySort,
} from "../lib/order-activity";
import {
  filterActivity,
  groupActivityByDay,
  revisionNumberByEntryId,
  segmentActivity,
} from "../lib/order-activity";
import type { MarketplaceFulfillmentViewer } from "../lib/fulfillment-badge-variant";
import { SCRIPT_REVIEW_ROUTE } from "../lib/preflight-report-path";
import type { OrderScriptPreflightComment } from "../types/marketplace.types";
import { StatusPill } from "./orders-list/StatusMenu";

interface OrderScriptActivityLogProps {
  entries: OrderScriptPreflightComment[];
  viewer?: MarketplaceFulfillmentViewer;
  className?: string;
  hideHeading?: boolean;
  sort?: ActivitySort;
  showFilters?: boolean;
  defaultFilter?: ActivityFilter;
  collapseSystemRuns?: boolean;
  onReactionChange?: (
    entry: OrderScriptPreflightComment,
    reactionType: ReactionType,
    active: boolean,
  ) => void;
  isReactionPending?: (
    entry: OrderScriptPreflightComment,
    reactionType: ReactionType,
  ) => boolean;
}

const REACTION_SYMBOLS: Record<ReactionType, string> = {
  LIKE: "👍",
  DISLIKE: "👎",
  LAUGH: "😄",
  CELEBRATE: "🎉",
  CONFUSED: "😕",
  LOVE: "❤️",
  SHIP_IT: "🚀",
  SEEN: "👀",
};

const REACTION_LABELS: Record<ReactionType, string> = {
  LIKE: "Like",
  DISLIKE: "Dislike",
  LAUGH: "Laugh",
  CELEBRATE: "Celebrate",
  CONFUSED: "Confused",
  LOVE: "Love",
  SHIP_IT: "Ship it",
  SEEN: "Seen",
};

function ActivityTimestamp({ iso }: { iso: string }) {
  const absolute = formatFullLocalDateTime(iso) ?? iso;
  const relative = formatRelativeOrDate(iso, Date.now());
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <time
          dateTime={iso}
          className="text-[11px] tabular-nums text-muted-foreground"
        >
          {relative}
        </time>
      </TooltipTrigger>
      <TooltipContent>{absolute}</TooltipContent>
    </Tooltip>
  );
}

function RevisionLabel({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count < 1) return null;
  return (
    <Badge
      variant="outline"
      className="text-caption border-border-subtle font-normal tabular-nums text-muted-foreground"
    >
      {t("marketplace.orders.activity.revisionRound", {
        count,
        defaultValue: "Revision {{count}}",
      })}
    </Badge>
  );
}

function revisionLabelCount(
  entryId: string,
  revisionById: ReadonlyMap<string, number>,
  viewer: MarketplaceFulfillmentViewer,
): number {
  if (viewer === "client") return 0;
  return revisionById.get(entryId) ?? 0;
}

function StatusChangeTrail({
  entry,
  viewer,
}: {
  entry: OrderScriptPreflightComment;
  viewer: MarketplaceFulfillmentViewer;
}) {
  if (!entry.fromStatus && !entry.toStatus) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {entry.fromStatus ? (
        <StatusPill status={entry.fromStatus} viewer={viewer} />
      ) : null}
      <Icon name="arrow-right" size="xs" color="muted" aria-hidden />
      {entry.toStatus ? (
        <StatusPill status={entry.toStatus} viewer={viewer} />
      ) : null}
    </span>
  );
}

function Note({
  entry,
  viewer,
  revisionById,
  onReactionChange,
  isReactionPending,
}: {
  entry: OrderScriptPreflightComment;
  viewer: MarketplaceFulfillmentViewer;
  revisionById: ReadonlyMap<string, number>;
  onReactionChange: OrderScriptActivityLogProps["onReactionChange"];
  isReactionPending: OrderScriptActivityLogProps["isReactionPending"];
}) {
  const { t } = useTranslation();
  const author =
    entry.author?.displayName?.trim() ||
    t("marketplace.orders.activity.unknownAuthor", "Unknown");
  const authorRole = entry.author?.role;
  const authorOrg = entry.author?.orgName?.trim() || null;
  const viewerRole = viewer === "ops" ? "admin" : viewer;
  const showAuthorRole = authorRole != null && authorRole !== viewerRole;
  const reactionGroups = new Map(
    (entry.reactions ?? []).map((group) => [group.reactionType, group]),
  );
  const reactionItems = REACTION_TYPES.map((reactionType) => {
    const group = reactionGroups.get(reactionType);
    const label = t(
      `marketplace.orders.activity.reactions.types.${reactionType}`,
      REACTION_LABELS[reactionType],
    );
    const count = group?.count ?? 0;
    const remainingActorCount = Math.max(
      0,
      count - (group?.actors.length ?? 0),
    );
    return {
      id: reactionType,
      symbol: REACTION_SYMBOLS[reactionType],
      count,
      pressed: group?.isCurrentPersonActive ?? false,
      pending: isReactionPending?.(entry, reactionType) ?? false,
      disabled: onReactionChange == null,
      addLabel: t("marketplace.orders.activity.reactions.add", {
        reaction: label,
        defaultValue: "Add {{reaction}}",
      }),
      removeLabel: t("marketplace.orders.activity.reactions.remove", {
        reaction: label,
        defaultValue: "Remove {{reaction}}",
      }),
      actorPreviewLabel: t(
        "marketplace.orders.activity.reactions.actorPreview",
        {
          reaction: label,
          defaultValue: "People who reacted with {{reaction}}",
        },
      ),
      actors: (group?.actors ?? []).map((actor, index) => ({
        id: `${reactionType}-${index}`,
        content: <ReactionActorLine actor={actor} />,
      })),
      moreActorsContent:
        remainingActorCount > 0
          ? t("marketplace.orders.activity.reactions.moreActors", {
              count: remainingActorCount,
              defaultValue: "and {{count}} more",
            })
          : undefined,
    };
  });

  return (
    <li className="flex gap-2.5">
      <UserAvatar
        name={author}
        size="sm"
        className="mt-0.5 size-6 text-[10px]"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[13px] font-medium text-foreground">
            {author}
          </span>
          {authorOrg ? (
            <span className="text-[11px] text-muted-foreground">
              {authorOrg}
            </span>
          ) : null}
          {showAuthorRole ? (
            <Badge variant="slate">
              {t(
                `marketplace.orders.activity.authorRole.${authorRole}`,
                authorRole === "admin"
                  ? "Admin"
                  : authorRole === "partner"
                    ? "Partner"
                    : "Client",
              )}
            </Badge>
          ) : null}
          <RevisionLabel
            count={revisionLabelCount(entry.id, revisionById, viewer)}
          />
          <ActivityTimestamp iso={entry.createdAt} />
        </div>
        <p className="text-drawer-body mt-1 max-h-72 w-fit max-w-full overflow-y-auto overscroll-contain whitespace-pre-wrap break-words rounded-md border border-border/50 bg-muted/40 px-2.5 py-1.5 text-foreground">
          {entry.body}
        </p>
        <ReactionBar
          items={reactionItems}
          addPickerLabel={t(
            "marketplace.orders.activity.reactions.openPicker",
            "Add reaction",
          )}
          className="mt-1.5"
          onPressedChange={(reactionType, active) =>
            onReactionChange?.(entry, reactionType as ReactionType, active)
          }
        />
      </div>
    </li>
  );
}

function ReactionActorLine({ actor }: { actor: ReactionActor }) {
  const { t } = useTranslation();
  const displayLabel =
    actor.displayKind === "former-user"
      ? t("marketplace.orders.activity.reactions.formerUser", "Former user")
      : actor.displayLabel?.trim() ||
        t("marketplace.orders.activity.unknownAuthor", "Unknown");
  const context = [actor.roleLabel, actor.organizationLabel]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex min-w-0 items-center gap-2">
      <UserAvatar
        name={displayLabel}
        size="sm"
        className="size-6 shrink-0 text-[10px]"
      />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-xs font-medium text-foreground">
          {displayLabel}
        </span>
        {context ? (
          <span className="truncate text-[11px] text-muted-foreground">
            {context}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function SystemEvent({
  entry,
  viewer,
  revisionById,
}: {
  entry: OrderScriptPreflightComment;
  viewer: MarketplaceFulfillmentViewer;
  revisionById: ReadonlyMap<string, number>;
}) {
  const { t } = useTranslation();
  const author =
    entry.author?.displayName?.trim() ||
    t("marketplace.orders.activity.unknownAuthor", "Unknown");
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-muted-foreground">
      <Icon name="history" size="xs" color="muted" aria-hidden />
      <span className="font-medium text-foreground">{author}</span>
      <span>
        {t("marketplace.orders.activity.statusChangedShort", "changed status")}
      </span>
      <StatusChangeTrail entry={entry} viewer={viewer} />
      <RevisionLabel
        count={revisionLabelCount(entry.id, revisionById, viewer)}
      />
      <ActivityTimestamp iso={entry.createdAt} />
      {entry.scriptReviewId && entry.toStatus !== "SENT_FOR_REVISION" ? (
        <Link
          to={SCRIPT_REVIEW_ROUTE}
          params={{ reviewId: entry.scriptReviewId }}
          className="font-medium text-primary hover:text-primary/90"
        >
          {t(
            "marketplace.orders.activity.viewPreflight",
            "View preflight submission",
          )}
        </Link>
      ) : null}
    </li>
  );
}

function SystemRun({
  segment,
  collapsedByDefault,
  viewer,
  revisionById,
}: {
  segment: Extract<ActivitySegment, { kind: "system" }>;
  collapsedByDefault: boolean;
  viewer: MarketplaceFulfillmentViewer;
  revisionById: ReadonlyMap<string, number>;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(!collapsedByDefault);
  const isCollapsible = segment.entries.length > 1;

  if (!isCollapsible || expanded) {
    return (
      <li className="flex flex-col gap-2">
        {isCollapsible ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="chevron-up"
            className="h-7 self-start px-1 text-caption text-muted-foreground"
            aria-expanded
            onClick={() => setExpanded(false)}
          >
            {t("marketplace.orders.activity.hideStatusUpdates", {
              count: segment.entries.length,
            })}
          </Button>
        ) : null}
        <ul className="flex flex-col gap-2">
          {segment.entries.map((entry) => (
            <SystemEvent
              key={entry.id}
              entry={entry}
              viewer={viewer}
              revisionById={revisionById}
            />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconStart="chevron-down"
        className="h-auto min-h-7 px-1 text-caption text-muted-foreground"
        aria-expanded={false}
        onClick={() => setExpanded(true)}
      >
        {t("marketplace.orders.activity.statusRunSummary", {
          count: segment.entries.length,
        })}
        <span className="tabular-nums">
          <ActivityTimestamp iso={segment.startAt} />
          {segment.startAt !== segment.endAt ? (
            <>
              <span aria-hidden>–</span>
              <ActivityTimestamp iso={segment.endAt} />
            </>
          ) : null}
        </span>
        {collapsedRevisionCounts(segment.entries, revisionById, viewer).map(
          (count) => (
            <RevisionLabel key={count} count={count} />
          ),
        )}
      </Button>
    </li>
  );
}

function collapsedRevisionCounts(
  entries: readonly OrderScriptPreflightComment[],
  revisionById: ReadonlyMap<string, number>,
  viewer: MarketplaceFulfillmentViewer,
): number[] {
  const seen = new Set<number>();
  const counts: number[] = [];
  for (const entry of entries) {
    const count = revisionLabelCount(entry.id, revisionById, viewer);
    if (count < 1 || seen.has(count)) continue;
    seen.add(count);
    counts.push(count);
  }
  return counts;
}

function ActivitySegments({
  entries,
  collapseSystemRuns,
  viewer,
  revisionById,
  onReactionChange,
  isReactionPending,
}: {
  entries: OrderScriptPreflightComment[];
  collapseSystemRuns: boolean;
  viewer: MarketplaceFulfillmentViewer;
  revisionById: ReadonlyMap<string, number>;
  onReactionChange: OrderScriptActivityLogProps["onReactionChange"];
  isReactionPending: OrderScriptActivityLogProps["isReactionPending"];
}) {
  return (
    <ul className="flex flex-col gap-3">
      {segmentActivity(entries).map((segment) =>
        segment.kind === "note" ? (
          <Note
            key={segment.entry.id}
            entry={segment.entry}
            viewer={viewer}
            revisionById={revisionById}
            onReactionChange={onReactionChange}
            isReactionPending={isReactionPending}
          />
        ) : (
          <SystemRun
            key={`run-${segment.entries.map((entry) => entry.id).join("-")}`}
            segment={segment}
            collapsedByDefault={collapseSystemRuns}
            viewer={viewer}
            revisionById={revisionById}
          />
        ),
      )}
    </ul>
  );
}

function formatDayLabel(
  dayKey: string,
  language: string,
  todayLabel: string,
  yesterdayLabel: string,
): string {
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${`${today.getMonth() + 1}`.padStart(2, "0")}-${`${today.getDate()}`.padStart(2, "0")}`;
  if (dayKey === todayKey) return todayLabel;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = `${yesterday.getFullYear()}-${`${yesterday.getMonth() + 1}`.padStart(2, "0")}-${`${yesterday.getDate()}`.padStart(2, "0")}`;
  if (dayKey === yesterdayKey) return yesterdayLabel;
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return dayKey;
  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

export function OrderScriptActivityLog({
  entries,
  viewer = "client",
  className,
  hideHeading = false,
  sort = "newest",
  showFilters = false,
  defaultFilter = "all",
  collapseSystemRuns = false,
  onReactionChange,
  isReactionPending,
}: OrderScriptActivityLogProps) {
  const { t, i18n } = useTranslation();
  const userEmail = useAuth((state) => state.user?.email);
  const filterStorageKey = `marketplace:activity-filter:${userEmail ?? "anonymous"}`;
  const [sortOrder, setSortOrder] = useState<ActivitySort>(sort);
  const [filter, setFilter] = useState<ActivityFilter>(() => {
    try {
      const stored = window.localStorage.getItem(filterStorageKey);
      return stored === "all" || stored === "notes" || stored === "status"
        ? stored
        : defaultFilter;
    } catch {
      return defaultFilter;
    }
  });
  const revisionById = revisionNumberByEntryId(entries);
  const filteredEntries = filterActivity(entries, filter);
  const groups = groupActivityByDay(filteredEntries, sortOrder);

  useEffect(() => {
    if (filter === "all" || entries.length === 0 || filteredEntries.length > 0)
      return;
    setFilter("all");
    try {
      window.localStorage.setItem(filterStorageKey, "all");
    } catch {
      // Storage is optional; the non-empty fallback still applies in memory.
    }
  }, [entries.length, filter, filterStorageKey, filteredEntries.length]);

  const handleFilterChange = useCallback(
    (nextFilter: ActivityFilter) => {
      setFilter(nextFilter);
      try {
        window.localStorage.setItem(filterStorageKey, nextFilter);
      } catch {
        // Storage is optional; the current session still remembers the filter.
      }
    },
    [filterStorageKey],
  );

  if (entries.length === 0) return null;

  return (
    <div className={className}>
      {hideHeading ? null : (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-drawer-body font-medium text-foreground">
              {t("marketplace.orders.activity.headingWithCount", {
                count: entries.length,
                defaultValue: "Activity · {{count}}",
              })}
            </p>
            <p className="mt-0.5 text-caption text-muted-foreground">
              {viewer === "partner"
                ? t(
                    "marketplace.orders.activity.sharedWithClient",
                    "Notes are visible to the client.",
                  )
                : t(
                    "marketplace.orders.activity.sharedWithPartner",
                    "Notes are visible to the production partner.",
                  )}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart={sortOrder === "newest" ? "arrow-down" : "arrow-up"}
            className="h-7 px-2 text-caption text-muted-foreground"
            onClick={() =>
              setSortOrder((current) =>
                current === "newest" ? "oldest" : "newest",
              )
            }
          >
            {sortOrder === "newest"
              ? t("marketplace.orders.activity.newestFirst")
              : t("marketplace.orders.activity.oldestFirst")}
          </Button>
        </div>
      )}
      {showFilters ? (
        <div
          role="group"
          aria-label={t("marketplace.orders.activity.filterLabel")}
          className="mb-3 flex items-center gap-1 border-b border-border pb-3"
        >
          {(["all", "notes", "status"] as const).map((option) => (
            <Button
              key={option}
              type="button"
              variant={filter === option ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2 text-caption"
              aria-pressed={filter === option}
              onClick={() => handleFilterChange(option)}
            >
              {t(`marketplace.orders.activity.filter.${option}`)}
            </Button>
          ))}
        </div>
      ) : null}
      {filteredEntries.length === 0 ? (
        <p className="text-caption text-muted-foreground">
          {t("marketplace.orders.activity.emptyFilter")}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section key={group.dayKey} className="flex flex-col gap-3">
              <h4 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
                {formatDayLabel(
                  group.dayKey,
                  i18n.language,
                  t("marketplace.orders.activity.today"),
                  t("marketplace.orders.activity.yesterday"),
                )}
              </h4>
              <ActivitySegments
                entries={group.entries}
                collapseSystemRuns={collapseSystemRuns}
                viewer={viewer}
                revisionById={revisionById}
                onReactionChange={onReactionChange}
                isReactionPending={isReactionPending}
              />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
